import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { SelectedFeatures } from '../../../configurator/ConfiguratorCoreState';
import { IApiClient } from '../../../data/client';
import { ModularConfigurationData, ModularProductFamilyData, ShoppingContext } from '../../../data/model';
import { SiteTranslation } from '../../../localization/SiteTranslation';
import { INavigationService } from '../../../shared/NavigationService';
import { IShareModularConfiguratorUrlProvider } from '../../../shared/ShareModularConfiguratorUrlProvider';
import ModularConfiguratorPageQuery from '../../ModularConfiguratorPageQuery';
import LayoutPickerState from './ModularConfigurator/LayoutPicker/LayoutPickerItem/LayoutPickerState';
import ModularComponentConfiguratorState from './ModularConfigurator/ModularComponentConfigurator/ModularComponentConfiguratorState';
import ModularConfiguratorState from './ModularConfigurator/ModularConfiguratorState';

export enum ModularConfiguratorMode {
  Component,
  Layout,
  Summary,
}

export interface UsedModularComponentConfigurationListItem {
  code: string;
  name: string;
  features: SelectedFeatures;
  counter: number;
}

class ModularConfiguratorCoordinatorState {
  @observable
  public isConfigurationInitiated: boolean = false;

  @observable
  public previousMode?: ModularConfiguratorMode;

  @observable
  public mode: ModularConfiguratorMode = ModularConfiguratorMode.Layout;

  public readonly configurator: ModularConfiguratorState;
  public readonly layoutPicker: LayoutPickerState;

  @computed
  public get isComponentModeActive() {
    return this.mode === ModularConfiguratorMode.Component;
  }

  @computed
  public get isLayoutModeActive() {
    return this.mode === ModularConfiguratorMode.Layout;
  }

  @computed
  public get isSummary() {
    return this.mode === ModularConfiguratorMode.Summary;
  }

  public get productFamilyName() {
    return this.productFamily.name;
  }

  @computed
  public get usedComponentConfigurations(): UsedModularComponentConfigurationListItem[] {
    return this.configurator.components.reduce<UsedModularComponentConfigurationListItem[]>(
      (accumulator, component) => {
        const indexOfComponentGroup = accumulator.findIndex((x) => x.code === component.coreState.code);

        if (indexOfComponentGroup === -1) {
          accumulator.push({
            code: component.coreState.code,
            name: component.name,
            features: component.coreState.selectedFeaturesVisuallyGrouped,
            counter: 1,
          });
        } else {
          accumulator[indexOfComponentGroup].counter++;
        }

        return accumulator;
      },
      [],
    );
  }

  public constructor(
    client: IApiClient,
    configurationUrlProvider: IShareModularConfiguratorUrlProvider,
    private readonly productFamily: ModularProductFamilyData,
    shoppingContext: ShoppingContext,
    public readonly translation: SiteTranslation,
    initialLayoutId: string,
    navigation: INavigationService,
    query: ModularConfiguratorPageQuery,
  ) {
    makeObservable(this);

    this.configurator = new ModularConfiguratorState(
      client,
      shoppingContext,
      translation,
      productFamily.id,
      navigation,
      configurationUrlProvider,
      query.debugMaterial,
    );
    this.layoutPicker = new LayoutPickerState(productFamily.layouts);
    this.layoutPicker.setActiveItemByPredicate((x) => x.id === initialLayoutId);

    this.resolveModeByQuery(query);

    reaction(() => this.configurator.selectedComponent, this.handleComponentSelectionChange);
  }

  public async loadConfigurationIntoConfigurator(configuration: ModularConfigurationData) {
    await this.configurator.loadConfiguration(configuration);
  }

  @action.bound
  public goBackToConfigurator() {
    this.setMode(this.previousMode ?? ModularConfiguratorMode.Layout);
    this.configurator.disableReadonlyMode();
    this.configurator.unselectComponent();
  }

  @action.bound
  public goToSummary() {
    this.configurator.enableReadonlyMode();
    this.configurator.generateConfigurationCode();
    this.setMode(ModularConfiguratorMode.Summary);
  }

  @action.bound
  public setComponentMode() {
    this.setMode(ModularConfiguratorMode.Component);
    this.isConfigurationInitiated = true;
    this.configurator.disableReadonlyMode();
  }

  @action.bound
  public setLayoutMode() {
    this.setMode(ModularConfiguratorMode.Layout);
    this.configurator.disableReadonlyMode();
    this.configurator.unselectComponent();
  }

  @action.bound
  private handleComponentSelectionChange(component: ModularComponentConfiguratorState) {
    if (this.configurator.readonly) {
      return;
    }

    if (!component) {
      this.configurator.activateConfigureAction();
      return;
    }

    if (this.mode === ModularConfiguratorMode.Layout) {
      this.setComponentMode();
    }
  }

  private resolveModeByQuery(query: ModularConfiguratorPageQuery) {
    const isRestoringConfiguration = Boolean(query.code || query.layoutCode);

    if (isRestoringConfiguration) {
      this.setComponentMode();
    } else {
      this.setLayoutMode();
    }
  }

  private setMode(mode: ModularConfiguratorMode) {
    this.previousMode = this.mode;
    this.mode = mode;
  }
}

export default ModularConfiguratorCoordinatorState;
